/* eslint-disable @reatom/unit-naming-rule */
import { atom, reaction } from '@reatom/framework'

import { AsyncStateN } from '@blade/std-lib'
import { CurrencyModel } from '@root/entities/currency'
import { nativePriceAtom } from '@root/features/realtime/atoms'
import { reatomQuotePolling } from '@root/features/trade/get-quote'

import type { WalletSwapInputModel } from './reatom-wallet-swap-input'
import type { WalletSwapPairModel } from './reatom-wallet-swap-pair'
import type { WalletSwapSettingsModel } from './reatom-wallet-swap-settings'
import type { WalletModel } from '@root/entities/wallet'
import type { QuoteInput } from '@root/features/trade/get-quote'

// TODO: Ideally we should stop polling once swap is done

export const reatomWalletSwapQuote = (
  swapInput: WalletSwapInputModel,
  swapPool: WalletSwapPairModel,
  settings: WalletSwapSettingsModel,
  wallet: WalletModel,
) => {
  const quoteInputAtom = atom((ctx): QuoteInput => {
    const pool = ctx.spy(swapPool.selectedPoolAtom)
    const currencyIn = (() => {
      const currencyIn_ = ctx.spy(swapInput.inCurrencyAtom)
      if (!currencyIn_ || !pool) {
        return undefined
      }
      if (CurrencyModel.isNativeOrWrapped(currencyIn_.network)(currencyIn_)) {
        return pool.base
      }
      return currencyIn_
    })()

    const currencyOut = (() => {
      const currencyOut_ = ctx.spy(swapInput.outCurrencyAtom)
      if (!currencyOut_ || !pool) {
        return undefined
      }
      if (CurrencyModel.isNativeOrWrapped(currencyOut_.network)(currencyOut_)) {
        return pool.base
      }
      return currencyOut_
    })()

    return {
      amount: ctx.spy(swapInput.inputAmountAtom),
      currencyIn,
      currencyOut,
      pool: ctx.spy(swapPool.selectedPoolAtom),
      slippage: ctx.spy(settings.swapSettingsAtom).slippage,
      type: ctx.spy(swapInput.inputTypeAtom),
      wallet,
    }
  }, 'quoteInputAtom')

  const quotePollingModel = reatomQuotePolling({
    inputAtom: quoteInputAtom,
    name: 'quotePollingModel',
    skipLoadingState: false,
  })
  const { quoteStateAtom } = quotePollingModel

  const inCurrencyPriceUsdAtom = atom((ctx) => {
    const inputCurrency = ctx.spy(swapInput.inCurrencyAtom)
    if (
      inputCurrency &&
      CurrencyModel.isNativeOrWrapped(inputCurrency.network)(inputCurrency)
    ) {
      return AsyncStateN.success(ctx.spy(nativePriceAtom))
    }
    const quoteState = ctx.spy(quoteStateAtom)
    return quoteState.pipe(AsyncStateN.map((it) => it.inputCurrencyPriceUsd))
  }, 'inCurrencyPriceUsdAtom')

  const outCurrencyPriceAtom = atom((ctx) => {
    const outputCurrency = ctx.spy(swapInput.outCurrencyAtom)
    if (
      outputCurrency &&
      CurrencyModel.isNativeOrWrapped(outputCurrency.network)(outputCurrency)
    ) {
      return AsyncStateN.success(ctx.spy(nativePriceAtom))
    }
    const quoteState = ctx.spy(quoteStateAtom)
    return quoteState.pipe(AsyncStateN.map((it) => it.outputCurrencyPriceUsd))
  }, 'outCurrencyPriceAtom')

  const watchQuoteStateReaction = reaction((ctx) => {
    const quoteState = ctx.spy(quoteStateAtom)

    if (AsyncStateN.isSuccess(quoteState)) {
      const quote = quoteState.value
      if (quote.type === 'EXACT_INPUT') {
        swapInput.outCurrencyInputModel.setAmountRawAction(
          ctx,
          quote.outputAmount.toFormat(),
        )
      } else {
        swapInput.inCurrencyInputModel.setAmountRawAction(
          ctx,
          quote.inputAmount.toFormat(),
        )
      }
    }
  }, 'watchQuoteStateReaction')

  return {
    ...quotePollingModel,
    inCurrencyPriceUsdAtom,
    outCurrencyPriceAtom,
    watchQuoteStateReaction,
  }
}

export type WalletSwapQuoteModel = ReturnType<typeof reatomWalletSwapQuote>
